import { useCallback, useState } from 'react';

import { link } from '@aline/routes';
import type { OpenDialogType } from 'src/components';
import { useSendForSignatureHelpers } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { openLink, useDispatchNotification } from 'src/helpers';
import { useCreateWorkflow } from 'src/workflow';

import { useGetUnassignedWorkflowPermissionsLazy } from '../assignment';

export function useStartS4SFlow() {
  const { createWorkflow, loading: creatingWorkflow } = useCreateWorkflow();
  const dispatchFailSnack = useDispatchNotification({
    message: 'Failed to create workflow.',
    variant: 'error',
  });
  const startS4SFlow = useCallback(
    async ({
      documentIds = [],
      workflowName,
    }: { documentIds?: string[]; workflowName?: string | null } = {}) => {
      const { data } = await createWorkflow({
        variables: {
          input: {
            documentIds,
            props: { name: workflowName ?? undefined },
          },
        },
        variant: Apollo.WorkflowVariant.Contract,
      });
      if (!data?.initializeWorkflow?.workflow?.id) return dispatchFailSnack();

      openLink(link.SendForSignatureWorkflow({ workflowId: data.initializeWorkflow.workflow.id }));
    },
    [createWorkflow, dispatchFailSnack]
  );

  return { startS4SFlow, startingS4SFlow: creatingWorkflow };
}

export function useSendForSignatureWithChecks({
  showNotReadyReasons,
  workflowId,
}: {
  workflowId: UUID;
  showNotReadyReasons?: boolean;
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { proceedWithSending } = useSendForSignatureHelpers(workflowId);
  const [openDialog, setOpenDialog] = useState<OpenDialogType | null>(null);
  const [getUnusedWorkflowActions] = Apollo.useGetUnusedWorkflowActionsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [getMissingWorkflowActions] = Apollo.useGetMissingWorkflowActionsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const { getUnassignedWorkflowPermissions } = useGetUnassignedWorkflowPermissionsLazy({
    fetchPolicy: 'network-only',
  });
  const sendForSignatureWithChecks = useCallback(async (): Promise<{ success: boolean }> => {
    setIsSubmitting(true);
    // We need to fetch these in onClick b/c they can change while a user adds/removes fields during the S4S flow.
    const [
      { data: missingActionsData },
      { data: unusedActionsData },
      unassignedWorkflowPermissions,
    ] = await Promise.all([
      getMissingWorkflowActions({ variables: { workflowId } }),
      getUnusedWorkflowActions({ variables: { workflowId } }),
      getUnassignedWorkflowPermissions({ variables: { workflowId } }),
    ]);
    if (missingActionsData?.workflow?.missingActions) {
      setIsSubmitting(false);
      setOpenDialog('missing-actions');
      return { success: false };
    }
    if (unusedActionsData?.workflow?.unusedActions?.length) {
      setIsSubmitting(false);
      setOpenDialog('unused-actions');
      return { success: false };
    }
    if (unassignedWorkflowPermissions.length) {
      setIsSubmitting(false);
      setOpenDialog('unassigned-roles');
      return { success: false };
    }

    const { data } = await proceedWithSending({ showNotReadyReasons });
    setIsSubmitting(false);
    return { success: Boolean(data) };
  }, [
    getMissingWorkflowActions,
    getUnassignedWorkflowPermissions,
    getUnusedWorkflowActions,
    proceedWithSending,
    showNotReadyReasons,
    workflowId,
  ]);

  return {
    isSubmitting,
    openDialog,
    sendForSignatureWithChecks,
    setIsSubmitting,
    setOpenDialog,
  };
}
